import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiSearch, FiPlus, FiMinus, FiTrash2, FiUserPlus, FiEdit2 } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import api, { ItemsAPI } from '../services/api';
import ItemCard from '../components/pos/ItemCard';
import EditItemModal from '../components/pos/EditItemModal';
import PaymentModal from '../components/PaymentModal';
import BillingHistoryPanel from '../components/pos/BillingHistoryPanel';
import { toNum } from '../utils/pricing';
import './POS.css';

const INDIAN_STATE_CODE_TO_NAME = {
  '01': 'Jammu & Kashmir',
  '02': 'Himachal Pradesh',
  '03': 'Punjab',
  '04': 'Chandigarh',
  '05': 'Uttarakhand',
  '06': 'Haryana',
  '07': 'Delhi',
  '08': 'Rajasthan',
  '09': 'Uttar Pradesh',
  '10': 'Bihar',
  '11': 'Sikkim',
  '12': 'Arunachal Pradesh',
  '13': 'Nagaland',
  '14': 'Manipur',
  '15': 'Mizoram',
  '16': 'Tripura',
  '17': 'Meghalaya',
  '18': 'Assam',
  '19': 'West Bengal',
  '20': 'Jharkhand',
  '21': 'Odisha',
  '22': 'Chhattisgarh',
  '23': 'Madhya Pradesh',
  '24': 'Gujarat',
  '26': 'Daman and Diu, Dadra & Nagar Haveli',
  '27': 'Maharashtra',
  '28': 'Andhra Pradesh (before bifurcation)',
  '29': 'Karnataka',
  '30': 'Goa',
  '31': 'Lakshadweep',
  '32': 'Kerala',
  '33': 'Tamil Nadu',
  '34': 'Puducherry',
  '35': 'Andaman & Nicobar Islands',
  '36': 'Telangana',
  '37': 'Andhra Pradesh (New)',
  '38': 'Ladakh'
};

const getStateDisplayName = (stateValue) => {
  const raw = String(stateValue || '').trim();
  if (!raw || raw === '-') return '';
  const normalizedCode = /^\d+$/.test(raw) ? raw.padStart(2, '0') : raw;
  return INDIAN_STATE_CODE_TO_NAME[normalizedCode] || raw;
};

export default function POS() {
  const { company } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all'); // all | product | service
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selected, setSelected] = useState(null); // item being edited
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [walkInId, setWalkInId] = useState(null);
  const [cart, setCart] = useState([]); // [{itemId, name, unit, price, qty}]
  const [companyData, setCompanyData] = useState(null);
  const [pulseItemId, setPulseItemId] = useState(null);
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', email: '', phone: '', gstin: '' });
  const [showEditCustomer, setShowEditCustomer] = useState(false);
  const [editCustomer, setEditCustomer] = useState({ id: '', name: '', email: '', phone: '', gstin: '' });
  const [searchingCustomer, setSearchingCustomer] = useState(false);
  const [customerPhoneQuery, setCustomerPhoneQuery] = useState('');
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerHighlight, setCustomerHighlight] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('amount'); // 'amount' | 'percent'
  const [payModal, setPayModal] = useState({ open: false });
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [itemImageUploading, setItemImageUploading] = useState(false);
  const itemImageInputRef = useRef(null);
  const WALK_IN_PHONE = '9999999999';
  const WALK_IN_NAME = 'Walk-in Customer';

  const handlePaymentSubmit = async (paymentData) => {
    try {
      // Build invoice payload similar to InvoiceForm
      const today = new Date().toISOString().slice(0,10);
      const printedAt = new Date().toISOString();
      // Backend enforces dueDate > date (strict). For POS immediate payments,
      // we still set a valid dueDate (tomorrow) to satisfy validation.
      const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const cust = customers.find((c) => String(c.id) === String(customerId)) || {};
      const itemsMap = new Map(items.map(it => [String(it.id), it]));
      // Use the same discount inputs shown on-screen (so POS, invoice PDF, and print preview match)
      const discountRaw = Number(discount || 0);
      const safeDocDiscountPercent = Math.max(0, Math.min(100, Number.isFinite(discountRaw) ? discountRaw : 0));
      const safeDocDiscountAmount = Math.max(0, Number.isFinite(discountRaw) ? discountRaw : 0);
      const docDisc =
        discountType === 'percent'
          ? { discountPercent: safeDocDiscountPercent, discountAmount: 0 }
          : { discountAmount: safeDocDiscountAmount, discountPercent: 0 };

      const lines = cart.map((l) => {
        const it = itemsMap.get(String(l.itemId)) || {};
        const qty = Number(l.qty || 0);
        const price = Number(l.price || 0);
        // Keep line discounts as entered (UI ensures only one of amount/percent is active)
        const discAmtLine = Math.max(0, Number(l.discountAmount || 0));
        const discPctLine = Math.max(0, Math.min(100, Number(l.discountPercent || 0)));
        // Include full tax rates from item master for GST compliance
        const cgstRate = Number(l.cgstRate ?? it.cgstRate ?? 0);
        const sgstRate = Number(l.sgstRate ?? it.sgstRate ?? 0);
        const igstRate = Number(l.igstRate ?? it.igstRate ?? (cgstRate + sgstRate));
        const cessRate = Number(l.cessRate ?? it.cessRate ?? 0);
        return {
          itemId: String(l.itemId),
          description: l.description || it.name || '',
          quantity: qty,
          unit: l.unit || it.unit || 'PCS',
          price: price,
          discountAmount: discAmtLine,
          discountPercent: discPctLine,
          cgstRate,
          sgstRate,
          igstRate,
          cessRate
        };
      });
      // Use enriched company profile (stateCode/gstin) for POS calculations and invoice payload.
      // Avoid shadowing the component state var `companyData`.
      const companyProfile = companyData || company || {};

      const payload = {
        customerId: String(customerId),
        date: today,
        dueDate: tomorrow,
        terms: 'IMMEDIATE',
        notes: '',
        items: lines,
        // POS is treated as local billing: force POS to the company state (so backend uses CGST+SGST).
        posStateCode: companyProfile?.stateCode || '',
        createdFrom: 'POS',
        ...docDisc
      };
      const { data: created } = await api.post('/api/invoices', payload);
      const invId = created?.id || created?.invoiceId;
      if (invId) {
        await api.post(`/api/invoices/${invId}/mark-paid`, paymentData);
      }
      setPayModal({ open: false });
      // Open printable preview. Prefer server truth (paymentDate, createdByUser, header totals).
      let invoiceNumber = created?.number || '';
      let snapshot = {
        printedAt,
        invoiceDate: today,
        posStateCode: companyProfile?.stateCode || '',
        paymentDateTime: printedAt,
        paymentMethod: paymentData?.paymentMethod || '',
        paymentReference: paymentData?.paymentReference || '',
        billedBy: '',
        company: companyProfile,
        customer: cust,
        items: cart.map(l => ({ ...l })),
        // Reuse the already computed POS GST totals so print matches the sidebar.
        taxTotals,
        // For receipt display (Option B): keep line discounts separate from doc discount.
        grossTotal,
        lineDiscountTotal,
        docDiscountAmount: discountAmount,
        discountAmount
      };
      if (invId) {
        try {
          const { data: invRes } = await api.get(`/api/invoices/${invId}`);
          const detail = invRes?.invoice || invRes || {};
          invoiceNumber = detail.number || invoiceNumber;
          const linesRaw = Array.isArray(detail.items) ? detail.items : (Array.isArray(detail.lineItems) ? detail.lineItems : []);
          const itemsForPrint = (linesRaw || []).map((l) => ({
            name: l.name || l.itemSnapshot?.name || l.itemName || l.description || 'Item',
            qty: Number(l.quantity ?? l.qty ?? 0),
            price: Number(l.price ?? l.rate ?? l.unitPrice ?? 0),
            discountAmount: Number(l.discountAmount ?? 0),
            discountPercent: Number(l.discountPercent ?? 0)
          }));
          const grossTotalSrv = itemsForPrint.reduce((acc, it) => acc + (Number(it.qty || 0) * Number(it.price || 0)), 0);
          const lineDiscountTotalSrv = itemsForPrint.reduce((acc, it) => {
            const gross = Number(it.qty || 0) * Number(it.price || 0);
            const discAmt = Math.max(0, Number(it.discountAmount || 0));
            const discPct = Math.max(0, Math.min(100, Number(it.discountPercent || 0)));
            const discFromPct = (discPct / 100) * gross;
            return acc + Math.min(gross, discAmt + discFromPct);
          }, 0);
          const netBeforeDocDiscount = itemsForPrint.reduce((acc, it) => {
            const gross = Number(it.qty || 0) * Number(it.price || 0);
            const discAmt = Math.max(0, Number(it.discountAmount || 0));
            const discPct = Math.max(0, Math.min(100, Number(it.discountPercent || 0)));
            const discFromPct = (discPct / 100) * gross;
            const lineDisc = Math.min(gross, discAmt + discFromPct);
            return acc + Math.max(0, gross - lineDisc);
          }, 0);
          const invDiscAmt = Math.max(0, Number(detail.discountAmount || 0));
          const invDiscPct = Math.max(0, Math.min(100, Number(detail.discountPercent || 0)));
          const docDiscAmt = invDiscPct > 0 ? +((netBeforeDocDiscount * invDiscPct) / 100).toFixed(2) : invDiscAmt;
          const taxTotalsFromHeader = {
            taxable: Number(detail.taxableValue || 0),
            cgst: Number(detail.cgstAmount || 0),
            sgst: Number(detail.sgstAmount || 0),
            igst: Number(detail.igstAmount || 0),
            cess: Number(detail.cessAmount || 0),
            grand: Number(detail.total || 0),
            supplyType: String(detail.supplyType || '').toUpperCase() || (Number(detail.igstAmount || 0) > 0 ? 'INTER' : 'INTRA')
          };
          const createdByUser = detail.createdByUser;
          const billedBy = (() => {
            if (createdByUser?.firstName || createdByUser?.lastName) return `${createdByUser.firstName || ''} ${createdByUser.lastName || ''}`.trim();
            if (createdByUser?.email) return createdByUser.email;
            if (detail.createdBy) return `User #${detail.createdBy}`;
            return '';
          })();
          snapshot = {
            printedAt,
            invoiceDate: detail.date || today,
            posStateCode: detail.posStateCode || companyProfile?.stateCode || '',
            paymentDateTime: detail.paymentDate || printedAt,
            paymentMethod: detail.paymentMethod || paymentData?.paymentMethod || '',
            paymentReference: detail.paymentReference || paymentData?.paymentReference || '',
            billedBy,
            company: companyProfile,
            customer: detail.customerSnapshot || cust,
            items: itemsForPrint,
            taxTotals: taxTotalsFromHeader,
            grossTotal: +grossTotalSrv.toFixed(2),
            lineDiscountTotal: +lineDiscountTotalSrv.toFixed(2),
            docDiscountAmount: +docDiscAmt.toFixed(2),
            discountAmount: +(Number(lineDiscountTotalSrv || 0) + Number(docDiscAmt || 0)).toFixed(2)
          };
        } catch (_) {
          // fallback to local snapshot
        }
      }
      setBillPreview({
        open: true,
        invoiceId: invId || null,
        invoiceNumber,
        snapshot
      });
      // Refresh Billing History (POS-only invoices) immediately after checkout.
      setHistoryRefreshKey((k) => k + 1);
      // Reset cart and customer
      setCart([]);
      setCustomerId(null);
      setDiscount(0);
      setDiscountType('amount');
    } catch (e) {
      console.error('Checkout failed', e);
    }
  };
  const [billPreview, setBillPreview] = useState({ open: false, invoiceId: null, invoiceNumber: '', snapshot: null });
  const [openDisc, setOpenDisc] = useState({});

  const controlStyle = { fontSize: 12, padding: '6px 8px', height: 36, minHeight: 36 };
  const smallBtn = { fontSize: 12, padding: '6px 10px', borderRadius: 8, height: 32, minHeight: 32, display: 'inline-flex', alignItems: 'center', gap: 6 };

  const fetchItems = async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const data = await ItemsAPI.list({ companyId: company.id });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load items', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    if (!company?.id) return;
    try {
      const { data } = await api.get('/api/companies/' + company.id + '/customers');
      const list = data?.customers || [];
      setCustomers(list);
      return list;
    } catch (e) {
      console.error('Failed to load customers', e);
      return [];
    }
  };

  const ensureWalkInCustomer = async (baseCustomers = []) => {
    if (!company?.id) return null;
    let list = Array.isArray(baseCustomers) ? baseCustomers : [];
    let walkIn = list.find((c) => String(c.phone || '').trim() === WALK_IN_PHONE);

    if (!walkIn) {
      try {
        const payload = {
          companyId: company.id,
          name: WALK_IN_NAME,
          phone: WALK_IN_PHONE,
          stateCode: companyData?.stateCode || ''
        };
        const { data } = await api.post('/api/customers', payload);
        walkIn = data?.customer || data || null;
      } catch (e) {
        console.error('Failed to ensure walk-in customer', e);
      }
    }

    if (!walkIn?.id) {
      const refreshed = await fetchCustomers();
      walkIn = (Array.isArray(refreshed) ? refreshed : []).find((c) => String(c.phone || '').trim() === WALK_IN_PHONE);
    } else {
      setCustomers((prev) => {
        const next = Array.isArray(prev) ? [...prev] : [];
        const idx = next.findIndex((c) => String(c.id) === String(walkIn.id));
        if (idx >= 0) {
          next[idx] = { ...next[idx], ...walkIn };
          return next;
        }
        return [walkIn, ...next];
      });
    }

    if (walkIn?.id) {
      const idStr = String(walkIn.id);
      setWalkInId(idStr);
      setCustomerId((prev) => prev || idStr);
      return idStr;
    }
    return null;
  };

  const fetchCompany = async () => {
    if (!company?.id) return;
    try {
      const { data: compRes } = await api.get('/api/companies/' + company.id);
      // API returns { is_success, company }, but in some contexts it may return the company directly
      const comp = compRes?.company || compRes;
      let enriched = comp || {};
      try {
        const { data: gstStatus } = await api.get(`/api/companies/${company.id}/gst-status`);
        enriched = {
          ...enriched,
          gstin: gstStatus.gstin ?? comp?.gstin,
          stateCode: gstStatus.stateCode ?? comp?.stateCode,
          hasGSTIN: !!gstStatus.hasGSTIN
        };
      } catch (_) {
        // use comp as-is
      }
      setCompanyData(enriched);
    } catch (e) {
      console.error('Failed to load company', e);
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchItems();
      await fetchCompany();
      const list = await fetchCustomers();
      await ensureWalkInCustomer(list);
    };
    init();
  }, [company?.id]);

  const categories = useMemo(() => {
    const all = items
      .map((it) => String(it?.category || '').trim())
      .filter(Boolean);
    return [...new Set(all)].sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (type === 'service' && !it.isService) return false;
      if (type === 'product' && it.isService) return false;
      if (categoryFilter !== 'all' && String(it?.category || '').trim() !== categoryFilter) return false;
      if (!q) return true;
      return (
        (it.name || '').toLowerCase().includes(q) ||
        (it.description || '').toLowerCase().includes(q) ||
        (it.hsnsac || '').toLowerCase().includes(q) ||
        (it.category || '').toLowerCase().includes(q)
      );
    });
  }, [items, search, type, categoryFilter]);

  const formatINR = (val) => {
    try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(Number(val || 0)); } catch { return val; }
  };

  const addToCart = (it) => {
    setCart((prev) => {
      const idx = prev.findIndex((l) => String(l.itemId) === String(it.id));
      if (idx === -1) {
        return [...prev, { itemId: it.id, name: it.name, unit: it.unit || '', price: Number(it.price || 0), qty: 1 }];
      }
      const next = [...prev];
      next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
      return next;
    });
    // Keep pulse on the last added item
    setPulseItemId(it.id);
  };

  const decQty = (itemId) => {
    setCart((prev) => {
      const next = prev.map((l) => String(l.itemId) === String(itemId) ? { ...l, qty: Math.max(0, l.qty - 1) } : l).filter((l) => l.qty > 0);
      return next;
    });
  };

  const incQty = (itemId) => {
    setCart((prev) => prev.map((l) => String(l.itemId) === String(itemId) ? { ...l, qty: l.qty + 1 } : l));
  };

  const removeLine = (itemId) => {
    setCart((prev) => prev.filter((l) => String(l.itemId) !== String(itemId)));
  };

  const grossTotal = useMemo(() => {
    return +cart.reduce((acc, l) => acc + Number(l.qty || 0) * Number(l.price || 0), 0).toFixed(2);
  }, [cart]);
  const lineDiscountTotal = useMemo(() => {
    let total = 0;
    for (const l of cart) {
      const qty = Number(l.qty || 0);
      const price = Number(l.price || 0);
      const gross = qty * price;
      const discAmt = Math.max(0, Number(l.discountAmount || 0));
      const discPct = Math.max(0, Math.min(100, Number(l.discountPercent || 0)));
      const discountFromPercent = (discPct / 100) * gross;
      const lineDiscount = Math.min(gross, discAmt + discountFromPercent);
      total += lineDiscount;
    }
    return +total.toFixed(2);
  }, [cart]);
  const totals = useMemo(() => {
    const net = +cart.reduce((acc, l) => {
      const qty = Number(l.qty || 0);
      const price = Number(l.price || 0);
      const gross = qty * price;
      const discAmt = Math.max(0, Number(l.discountAmount || 0));
      const discPct = Math.max(0, Math.min(100, Number(l.discountPercent || 0)));
      const discountFromPercent = (discPct / 100) * gross;
      const lineDiscount = Math.min(gross, discAmt + discountFromPercent);
      const netLine = Math.max(0, gross - lineDiscount);
      return acc + netLine;
    }, 0).toFixed(2);
    return { subTotal: net, grandTotal: net };
  }, [cart]);

  const taxTotals = useMemo(() => {
    if (!cart.length) return { taxable: 0, cgst: 0, sgst: 0, igst: 0, cess: 0, grand: 0, supplyType: 'INTRA' };
    // POS is always local billing (Intrastate): force CGST + SGST (never IGST).
    const companyProfile = companyData || company || {};
    const hasGstin = !!(companyProfile?.gstin && String(companyProfile.gstin).trim());
    const r2 = (n) => +toNum(n).toFixed(2);

    // First pass: compute line taxable + line taxes (rounded) using only line-level discounts.
    const lineCalcs = cart.map((l) => {
      const it = items.find((x) => String(x.id) === String(l.itemId)) || {};
      const qty = toNum(l.qty);
      const price = toNum(l.price);
      const gross = qty * price;
      const discAmt = Math.max(0, toNum(l.discountAmount));
      const discPct = Math.max(0, Math.min(100, toNum(l.discountPercent)));
      const discFromPct = (discPct / 100) * gross;
      const netBeforeTax = Math.max(0, gross - discAmt - discFromPct);

      const cg = hasGstin ? toNum(it.cgstRate) : 0;
      const sg = hasGstin ? toNum(it.sgstRate) : 0;
      const cs = hasGstin ? toNum(it.cessRate) : 0;

      const cgstAmt = hasGstin ? r2((netBeforeTax * cg) / 100) : 0;
      const sgstAmt = hasGstin ? r2((netBeforeTax * sg) / 100) : 0;
      const cessAmt = hasGstin ? r2((netBeforeTax * cs) / 100) : 0;

      return {
        taxable: r2(netBeforeTax),
        cgst: cgstAmt,
        sgst: sgstAmt,
        igst: 0,
        cess: cessAmt
      };
    });

    const baseTaxable = r2(lineCalcs.reduce((acc, ln) => acc + toNum(ln.taxable), 0));

    // Document-level discount (applied before tax) uses the same base as backend.
    const discountRaw = (() => {
      const base = baseTaxable;
      const raw = toNum(discount);
      if (discountType === 'percent') {
        const pct = Math.max(0, Math.min(100, raw));
        return r2((base * pct) / 100);
      }
      return r2(Math.max(0, raw));
    })();
    const docDisc = Math.min(baseTaxable, discountRaw);
    const factor = baseTaxable > 0 ? +((baseTaxable - docDisc) / baseTaxable).toFixed(6) : 1;

    // Second pass: scale each line proportionally, rounding per-line, then sum (matches backend).
    let taxable = 0, cgst = 0, sgst = 0, igst = 0, cess = 0;
    for (const ln of lineCalcs) {
      taxable += r2(toNum(ln.taxable) * factor);
      cgst += r2(toNum(ln.cgst) * factor);
      sgst += r2(toNum(ln.sgst) * factor);
      igst += r2(toNum(ln.igst) * factor);
      cess += r2(toNum(ln.cess) * factor);
    }
    taxable = r2(taxable);
    cgst = r2(cgst);
    sgst = r2(sgst);
    igst = r2(igst);
    cess = r2(cess);

    const grand = r2(taxable + cgst + sgst + igst + cess);
    return { taxable, cgst, sgst, igst, cess, grand, supplyType: 'INTRA' };
  }, [cart, items, companyData, company, discount, discountType]);

  const discountAmount = useMemo(() => {
    const base = Number(totals.subTotal || 0);
    const raw = Number(discount || 0);
    if (discountType === 'percent') {
      const pct = Math.max(0, Math.min(100, raw));
      return +((base * pct) / 100).toFixed(2);
    }
    return +Math.max(0, raw).toFixed(2);
  }, [discount, discountType, totals.subTotal]);

  // Customer validation helpers
  const isValidEmail = (val) => {
    if (!val) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(val).trim());
  };
  const isValidPhone10 = (val) => {
    if (!val) return true;
    return /^\d{10}$/.test(String(val).trim());
  };
  const newCustEmailInvalid = !!newCustomer.email && !isValidEmail(newCustomer.email);
  const newCustPhoneInvalid = !!newCustomer.phone && !isValidPhone10(newCustomer.phone);
  const newCustHasIdentifier = !!(newCustomer.email?.trim() || newCustomer.phone?.trim());
  const editCustEmailInvalid = !!editCustomer.email && !isValidEmail(editCustomer.email);
  const editCustPhoneInvalid = !!editCustomer.phone && !isValidPhone10(editCustomer.phone);

  const handleCreateCustomer = async () => {
    if (!company?.id) return;
    if (!newCustomer.name.trim()) return;
    if (!newCustHasIdentifier) return;
    setCreatingCustomer(true);
    try {
      const payload = { ...newCustomer, companyId: company.id, stateCode: companyData?.stateCode || '' };
      const { data } = await api.post('/api/customers', payload);
      // Prefer returned customer; fallback to re-fetch
      await fetchCustomers();
      const createdId = data?.customer?.id || data?.id;
      if (createdId) setCustomerId(String(createdId));
      setShowCreateCustomer(false);
      setNewCustomer({ name: '', email: '', phone: '', gstin: '' });
    } catch (e) {
      console.error('Failed to create customer', e);
      // Soft-close but keep values for correction
    } finally {
      setCreatingCustomer(false);
    }
  };

  const handleOpenEditCustomer = () => {
    const cust = customers.find((c) => String(c.id) === String(customerId));
    if (!cust) return;
    setEditCustomer({ id: cust.id, name: cust.name || '', email: cust.email || '', phone: cust.phone || '', gstin: cust.gstin || '' });
    setShowEditCustomer(true);
  };

  const handleSaveEditCustomer = async () => {
    if (!editCustomer.id) return;
    try {
      const payload = { name: editCustomer.name, email: editCustomer.email, phone: editCustomer.phone, gstin: editCustomer.gstin, stateCode: companyData?.stateCode || '' };
      // Prefer company-scoped endpoint as used in Customers.js
      if (company?.id) {
        await api.put(`/api/companies/${company.id}/customers/${editCustomer.id}`, payload);
      } else {
        await api.put(`/api/customers/${editCustomer.id}`, payload);
      }
      // Optimistically update local state
      setCustomers((prev) => prev.map((c) => String(c.id) === String(editCustomer.id) ? { ...c, ...payload } : c));
      await fetchCustomers();
      setShowEditCustomer(false);
    } catch (e) {
      console.error('Failed to update customer', e);
    }
  };

  const handleSearchCustomerByPhone = async () => {
    if (!company?.id) return;
    const q = (customerPhoneQuery || '').trim();
    if (!q) return;
    setSearchingCustomer(true);
    try {
      const { data } = await api.get('/api/customers', { params: { phone: q, companyId: company.id } });
      const list = Array.isArray(data?.customers) ? data.customers : (Array.isArray(data) ? data : []);
      if (Array.isArray(list)) setCustomers(list);
    } catch (e) {
      console.error('Phone search failed', e);
    } finally {
      setSearchingCustomer(false);
    }
  };

  const handleProofFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setPayModal((m) => ({ ...m, uploading: true }));
      const resp = await api.get('/api/upload/presigned-url', { params: { fileType: file.type } });
      const { uploadUrl, fileUrl } = resp.data;
      await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      setPayModal((m) => ({ ...m, proofUrl: fileUrl || uploadUrl.split('?')[0], uploading: false }));
    } catch (e) {
      console.error('Upload failed', e);
      setPayModal((m) => ({ ...m, uploading: false }));
    }
  };

  const openPosBillFromInvoice = async (inv) => {
    try {
      const id = inv?.id;
      if (!id) return;
      const { data } = await api.get(`/api/invoices/${id}`);
      const detail = data?.invoice || data || {};
      const linesRaw = Array.isArray(detail.items) ? detail.items : (Array.isArray(detail.lineItems) ? detail.lineItems : []);
      const items = (linesRaw || []).map((l) => ({
        name: l.name || l.itemSnapshot?.name || l.itemName || l.description || 'Item',
        qty: Number(l.quantity ?? l.qty ?? 0),
        price: Number(l.price ?? l.rate ?? l.unitPrice ?? 0),
        discountAmount: Number(l.discountAmount ?? 0),
        discountPercent: Number(l.discountPercent ?? 0)
      }));

      const taxTotalsFromHeader = {
        taxable: Number(detail.taxableValue || 0),
        cgst: Number(detail.cgstAmount || 0),
        sgst: Number(detail.sgstAmount || 0),
        igst: Number(detail.igstAmount || 0),
        cess: Number(detail.cessAmount || 0),
        grand: Number(detail.total || 0),
        supplyType: String(detail.supplyType || '').toUpperCase() || (Number(detail.igstAmount || 0) > 0 ? 'INTER' : 'INTRA')
      };

      // Estimate total discount shown on bill (line + doc) for display.
      const lineDiscountTotal = items.reduce((acc, it) => {
        const gross = Number(it.qty || 0) * Number(it.price || 0);
        const discAmt = Math.max(0, Number(it.discountAmount || 0));
        const discPct = Math.max(0, Math.min(100, Number(it.discountPercent || 0)));
        const discFromPct = (discPct / 100) * gross;
        return acc + Math.min(gross, discAmt + discFromPct);
      }, 0);
      const netBeforeDocDiscount = items.reduce((acc, it) => {
        const gross = Number(it.qty || 0) * Number(it.price || 0);
        const discAmt = Math.max(0, Number(it.discountAmount || 0));
        const discPct = Math.max(0, Math.min(100, Number(it.discountPercent || 0)));
        const discFromPct = (discPct / 100) * gross;
        const lineDisc = Math.min(gross, discAmt + discFromPct);
        return acc + Math.max(0, gross - lineDisc);
      }, 0);
      const invDiscAmt = Math.max(0, Number(detail.discountAmount || 0));
      const invDiscPct = Math.max(0, Math.min(100, Number(detail.discountPercent || 0)));
      const docDisc = invDiscPct > 0 ? +((netBeforeDocDiscount * invDiscPct) / 100).toFixed(2) : invDiscAmt;
      const discountShown = +(lineDiscountTotal + docDisc).toFixed(2);
      const grossTotal = items.reduce((acc, it) => acc + (Number(it.qty || 0) * Number(it.price || 0)), 0);
      const printedAt = new Date().toISOString();
      const createdByUser = detail.createdByUser;
      const billedBy = (() => {
        if (createdByUser?.firstName || createdByUser?.lastName) return `${createdByUser.firstName || ''} ${createdByUser.lastName || ''}`.trim();
        if (createdByUser?.email) return createdByUser.email;
        if (detail.createdBy) return `User #${detail.createdBy}`;
        return '';
      })();

      setBillPreview({
        open: true,
        invoiceId: id,
        invoiceNumber: detail.number || inv.number || '',
        snapshot: {
          printedAt,
          invoiceDate: detail.date || '',
          posStateCode: detail.posStateCode || '',
          paymentDateTime: detail.paymentDate || '',
          paymentMethod: detail.paymentMethod || '',
          paymentReference: detail.paymentReference || '',
          billedBy,
          company: companyData || company || {},
          customer: detail.customerSnapshot || inv.customerSnapshot || {},
          items,
          taxTotals: taxTotalsFromHeader,
          grossTotal,
          lineDiscountTotal,
          docDiscountAmount: docDisc,
          discountAmount: discountShown
        }
      });
    } catch (e) {
      console.error('Failed to open POS bill', e);
      if (window.toast?.call) window.toast('Failed to open POS bill');
    }
  };

  const handleAddItemImage = () => {
    if (itemImageUploading) return;
    itemImageInputRef.current?.click();
  };

  const handleRemoveItemImage = (index) => {
    setSelected((prev) => {
      if (!prev) return prev;
      const currentImages = Array.isArray(prev.images) ? prev.images : [];
      return { ...prev, images: currentImages.filter((_, i) => i !== index) };
    });
  };

  const handleItemImageFile = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    try {
      setItemImageUploading(true);
      const uploaded = [];
      for (const file of files) {
        const resp = await api.get('/api/upload/presigned-url', {
          params: { fileType: file.type || 'image/jpeg' }
        });
        const { uploadUrl, fileUrl } = resp.data || {};
        if (!uploadUrl) continue;
        await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'image/jpeg' },
          body: file
        });
        uploaded.push(fileUrl || uploadUrl.split('?')[0]);
      }
      if (uploaded.length) {
        setSelected((prev) => {
          if (!prev) return prev;
          const currentImages = Array.isArray(prev.images) ? prev.images : [];
          return { ...prev, images: [...currentImages, ...uploaded] };
        });
      }
    } catch (e) {
      console.error('Item image upload failed', e);
    } finally {
      setItemImageUploading(false);
      if (event.target) event.target.value = '';
    }
  };

  return (
    <div className="page-content">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 6 }}>
        <h2 style={{ fontSize: 18, margin: 0 }}>POS</h2>
      </div>

      {/* Two column layout: Items (left) and Billing (right) */}
      <div className="form-section" style={{ display:'grid', gridTemplateColumns:'1fr 420px', gap: 12 }}>
        <div style={{ border:'1px solid #e5e7eb', borderRadius: 10, padding: 12, background:'#fff' }}>
          {/* Controls */}
          <div className="form-row" style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'nowrap' }}>
            <div className="form-group" style={{ flex: 1, minWidth: 0 }}>
              <div style={{ position:'relative' }}>
                <input aria-label="Search Items" className="form-control" style={{ ...controlStyle, paddingLeft: 30 }} placeholder="Name / description / HSN-SAC" value={search} onChange={(e) => setSearch(e.target.value)} />
                <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#6b7280' }}><FiSearch /></span>
              </div>
            </div>
            <select aria-label="Type" className="form-control" style={{ ...controlStyle, width: 140 }} value={type} onChange={(e) => setType(e.target.value)}>
              <option value="all">All</option>
              <option value="product">Products</option>
              <option value="service">Services</option>
            </select>
            <select
              aria-label="Category"
              className="form-control"
              style={{ ...controlStyle, width: 180 }}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">All Categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <button className="btn btn-secondary" style={smallBtn} onClick={() => { setSearch(''); setCategoryFilter('all'); }}>Reset</button>
            <button
              className="btn btn-primary"
              style={smallBtn}
              onClick={() => setSelected({ name:'', isService:false, unit:'Piece', price:0, inventory:0, hsnsac:'', category:'', cgstRate:0, sgstRate:0, igstRate:0, cessRate:0, description:'' })}
              title="Create new item"
            >
              <FiPlus /> New Item
            </button>
          </div>
          {/* Grid */}
          {loading ? (
            <div style={{ padding: 12, textAlign:'center' }}>
              <div className="loading-spinner" />
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
              {filtered.map((it) => (
                <ItemCard
                  key={it.id}
                  item={it}
                  pulse={cart.some((l) => String(l.itemId) === String(it.id))}
                  qtyInCart={
                    (cart.find((l) => String(l.itemId) === String(it.id))?.qty) || 0
                  }
                  onAdd={() => addToCart(it)}
                  onIncrement={() => {
                    const exists = cart.some((l) => String(l.itemId) === String(it.id));
                    if (exists) {
                      incQty(it.id);
                    } else {
                      addToCart(it);
                    }
                  }}
                  onDecrement={() => {
                    decQty(it.id);
                  }}
                  onEdit={() => setSelected({ ...it })}
                />
              ))}
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div style={{ padding: 12, textAlign:'center', color:'#6b7280', fontSize: 12 }}>No items found.</div>
          )}
        </div>

        {/* Billing Panel */}
        <div style={{ border:'1px solid #e5e7eb', borderRadius: 10, padding: 12, background:'#fff', position:'sticky', top: 12, height:'fit-content' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Bill</div>
          </div>
            <div className="form-group" style={{ marginTop: 8 }}>
            <label style={{ fontSize: 12 }}>Customer</label>
            <div style={{ position:'relative', display:'flex', gap:8, alignItems:'center' }}>
              <div style={{ position:'relative', flex:1 }}>
                <input
                  className="form-control"
                  style={{ ...controlStyle, width: '100%', borderColor: customerHighlight ? '#ef4444' : undefined, boxShadow: customerHighlight ? '0 0 0 3px rgba(239,68,68,0.15)' : undefined }}
                  placeholder="Search customer"
                  autoComplete="off"
                  spellCheck={false}
                  autoCorrect="off"
                  autoCapitalize="off"
                  name="customerSearch"
                  value={customerSearchQuery || (customers.find(c => String(c.id) === String(customerId))?.name || '')}
                  onChange={(e) => { setCustomerSearchQuery(e.target.value); setCustomerSearchOpen(true); if (customerHighlight) setCustomerHighlight(false); }}
                  onFocus={() => { setCustomerSearchOpen(true); if (customerHighlight) setCustomerHighlight(false); }}
                  onBlur={() => setTimeout(() => setCustomerSearchOpen(false), 150)}
                />
                {customerSearchOpen && (
                  <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1px solid #e5e7eb', borderTop:'none', zIndex:20, maxHeight:200, overflowY:'auto', borderRadius:'0 0 8px 8px' }}>
                    <div
                      style={{ padding:'8px 10px', cursor:'pointer', borderBottom:'1px solid #f3f4f6', fontSize:12, color:'#374151' }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={async () => {
                        const resolvedWalkInId = walkInId || await ensureWalkInCustomer(customers);
                        if (resolvedWalkInId) setCustomerId(String(resolvedWalkInId));
                        setCustomerSearchQuery('');
                        setShowEditCustomer(false);
                        setCustomerSearchOpen(false);
                        if (customerHighlight) setCustomerHighlight(false);
                      }}
                    >
                      Walk-in
                    </div>
                    {customers
                      .filter((c) => {
                        const isWalkIn = String(c.id) === String(walkInId) || String(c.phone || '').trim() === WALK_IN_PHONE;
                        if (isWalkIn) return false;
                        const q = customerSearchQuery.trim().toLowerCase();
                        if (!q) return true;
                        return (c.name || '').toLowerCase().includes(q) ||
                               (c.email || '').toLowerCase().includes(q) ||
                               (c.phone || '').toLowerCase().includes(q);
                      })
                      .slice(0, 50)
                      .map((c) => (
                        <div
                          key={c.id}
                          style={{ padding:'8px 10px', cursor:'pointer', borderBottom:'1px solid #f3f4f6', fontSize:12 }}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setCustomerId(String(c.id));
                            setEditCustomer({ id: c.id, name: c.name || '', email: c.email || '', phone: c.phone || '', gstin: c.gstin || '' });
                            setShowEditCustomer(false);
                            setCustomerSearchQuery('');
                            setCustomerSearchOpen(false);
                            if (customerHighlight) setCustomerHighlight(false);
                          }}
                        >
                          <div style={{ fontWeight:600, color:'#111827' }}>{c.name}</div>
                          <div style={{ color:'#6b7280', fontSize:11 }}>{c.email || '-'} • {c.phone || '-'}</div>
                        </div>
                      ))}
                    {customers.length === 0 && (
                      <div style={{ padding:'8px 10px', fontSize:12, color:'#6b7280' }}>No customers</div>
                    )}
                  </div>
                )}
              </div>
                <button className="btn btn-secondary" style={smallBtn} onClick={() => { setShowCreateCustomer(true); setShowEditCustomer(false); }}>
                <FiUserPlus /> New
              </button>
                <button
                  className="btn btn-secondary"
                  style={smallBtn}
                  onClick={handleOpenEditCustomer}
                  disabled={!customerId}
                  title="Edit customer"
                >
                  <FiEdit2 /> Edit
                </button>
            </div>
          </div>
          {/* Inline Customer Create/Edit Forms */}
          {showCreateCustomer && (
            <div style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:8, padding:8, marginTop:8 }}>
              <div style={{ fontWeight:600, fontSize:12, marginBottom:6 }}>New Customer</div>
              <div className="form-row" style={{ display:'grid', gridTemplateColumns:'1fr', gap:8 }}>
                <input className="form-control" style={controlStyle} placeholder="Name" value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} />
                <div>
                  <input className="form-control" style={{ ...controlStyle, borderColor: newCustEmailInvalid ? '#ef4444' : undefined }} placeholder="Email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} />
                  {newCustEmailInvalid && <div style={{ color:'#991b1b', fontSize: 11, marginTop: 4 }}>Enter a valid email address</div>}
                </div>
                <div>
                  <input className="form-control" style={{ ...controlStyle, borderColor: newCustPhoneInvalid ? '#ef4444' : undefined }} placeholder="Phone (10 digits)" value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} />
                  {newCustPhoneInvalid && <div style={{ color:'#991b1b', fontSize: 11, marginTop: 4 }}>Enter a 10-digit phone number</div>}
                </div>
                <input className="form-control" style={controlStyle} placeholder="GSTIN (optional)" value={newCustomer.gstin} onChange={(e) => setNewCustomer({ ...newCustomer, gstin: e.target.value })} />
              </div>
              <div style={{ marginTop:8, display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button className="btn btn-secondary" style={smallBtn} onClick={() => setShowCreateCustomer(false)}>Cancel</button>
                <button className="btn btn-primary" style={smallBtn} disabled={creatingCustomer || !newCustomer.name.trim() || !newCustHasIdentifier || newCustEmailInvalid || newCustPhoneInvalid} onClick={handleCreateCustomer}>
                  {creatingCustomer ? 'Saving…' : 'Create'}
                </button>
              </div>
            </div>
          )}
          {showEditCustomer && (
            <div style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:8, padding:8, marginTop:8 }}>
              <div style={{ fontWeight:600, fontSize:12, marginBottom:6 }}>Edit Customer</div>
              <div className="form-row" style={{ display:'grid', gridTemplateColumns:'1fr', gap:8 }}>
                <input className="form-control" style={controlStyle} placeholder="Name" value={editCustomer.name} onChange={(e) => setEditCustomer({ ...editCustomer, name: e.target.value })} />
                <div>
                  <input className="form-control" style={{ ...controlStyle, borderColor: editCustEmailInvalid ? '#ef4444' : undefined }} placeholder="Email" value={editCustomer.email} onChange={(e) => setEditCustomer({ ...editCustomer, email: e.target.value })} />
                  {editCustEmailInvalid && <div style={{ color:'#991b1b', fontSize: 11, marginTop: 4 }}>Enter a valid email address</div>}
                </div>
                <div>
                  <input className="form-control" style={{ ...controlStyle, borderColor: editCustPhoneInvalid ? '#ef4444' : undefined }} placeholder="Phone (10 digits)" value={editCustomer.phone} onChange={(e) => setEditCustomer({ ...editCustomer, phone: e.target.value })} />
                  {editCustPhoneInvalid && <div style={{ color:'#991b1b', fontSize: 11, marginTop: 4 }}>Enter a 10-digit phone number</div>}
                </div>
                <input className="form-control" style={controlStyle} placeholder="GSTIN (optional)" value={editCustomer.gstin} onChange={(e) => setEditCustomer({ ...editCustomer, gstin: e.target.value })} />
              </div>
              <div style={{ marginTop:8, display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button className="btn btn-secondary" style={smallBtn} onClick={() => setShowEditCustomer(false)}>Cancel</button>
                <button className="btn btn-primary" style={smallBtn} onClick={handleSaveEditCustomer} disabled={editCustEmailInvalid || editCustPhoneInvalid || !editCustomer.name.trim()}>
                  Save
                </button>
              </div>
            </div>
          )}
          {/* Removed separate phone search; dropdown is now searchable */}
          {/* {(() => {
            const cust = customers.find((c) => String(c.id) === String(customerId));
            if (!cust) return null;
            return (
              <div style={{ fontSize: 11, color:'#374151', background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:8, padding:8, marginTop:8 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
                  <div><span style={{ color:'#6b7280' }}>GSTIN:</span> {cust.gstin || '-'}</div>
                  <div><span style={{ color:'#6b7280' }}>State Code:</span> {cust.stateCode || '-'}</div>
                </div>
              </div>
            );
          })()} */}
          <div style={{ borderTop:'1px dashed #e5e7eb', margin:'8px 0' }} />
          <div style={{ maxHeight: 360, overflowY:'auto' }}>
            {cart.length === 0 ? (
              <div style={{ color:'#6b7280', fontSize: 12, textAlign:'center', padding:'8px 0' }}>Tap an item to add it to the bill</div>
            ) : (
              cart.map((l) => (
                <div
                  key={l.itemId}
                  style={{
                    display:'grid',
                    gridTemplateColumns:'1fr 64px 90px 56px 28px',
                    gap:8,
                    alignItems:'center',
                    padding:'6px 0',
                    borderBottom:'1px solid #f3f4f6'
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{l.name}</div>
                    <div style={{ fontSize: 11, color:'#6b7280' }}>{l.unit || 'Unit'} • {formatINR(l.price)}</div>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="form-control"
                    style={{ ...controlStyle, width: '100%', textAlign:'right' }}
                    value={l.qty}
                    onChange={(e) => {
                      const next = Math.max(0, parseInt(e.target.value || '0', 10));
                      setCart((prev) => {
                        const updated = prev.map((x) => String(x.itemId) === String(l.itemId) ? { ...x, qty: next } : x)
                          .filter((x) => x.qty > 0);
                        return updated;
                      });
                    }}
                  />
                  <div style={{ fontWeight: 700, fontSize: 12, textAlign:'right' }}>
                    {(() => {
                      const qty = Number(l.qty || 0);
                      const price = Number(l.price || 0);
                      const gross = qty * price;
                      const discAmt = Math.max(0, Number(l.discountAmount || 0));
                      const discPct = Math.max(0, Math.min(100, Number(l.discountPercent || 0)));
                      const discountFromPercent = (discPct / 100) * gross;
                      const lineDiscount = Math.min(gross, discAmt + discountFromPercent);
                      const net = Math.max(0, gross - lineDiscount);
                      return formatINR(net);
                    })()}
                  </div>
                  <button
                    className="btn btn-secondary"
                    style={{ ...smallBtn, height: 28, padding:'0 8px',border:'none' }}
                    onClick={() => setOpenDisc((m) => ({ ...m, [String(l.itemId)]: !m[String(l.itemId)] }))}
                    title="Add discount"
                  >
                    + Disc
                  </button>
                  <button
                    className="btn btn-outline-primary"
                    style={{ ...smallBtn, height: 28, padding:'0 8px', border:'none' }}
                    onClick={() => removeLine(l.itemId)}
                    title="Remove"
                  >
                    <FiTrash2 />
                  </button>
                  {!!openDisc[String(l.itemId)] && (
                    <div style={{ gridColumn: '1 / -1', background:'#fafafa', border:'1px solid #e5e7eb', borderRadius:8, padding:6 }}>
                      {(() => {
                        const currentDiscType = (Number(l.discountPercent || 0) > 0 && Number(l.discountAmount || 0) === 0) ? 'percent' : 'amount';
                        const discValue = currentDiscType === 'percent' ? (l.discountPercent ?? '') : (l.discountAmount ?? '');
                        return (
                          <div style={{ display:'grid', gridTemplateColumns:'auto 100px 90px', gap:8, alignItems:'center' }}>
                            <div style={{ fontSize: 11, color:'#6b7280' }}>Discount for {l.name}</div>
                            <input
                              className="form-control"
                              style={{ ...controlStyle, width: '100%', textAlign:'right' }}
                              type="number"
                              min="0"
                              step={currentDiscType === 'percent' ? '1' : '0.01'}
                              placeholder={currentDiscType === 'percent' ? 'Disc %' : 'Disc Amt'}
                              title={currentDiscType === 'percent' ? 'Discount percentage' : 'Flat discount amount'}
                              value={discValue}
                              onChange={(e) => {
                                const v = e.target.value;
                                setCart((prev) => prev.map((x) => {
                                  if (String(x.itemId) !== String(l.itemId)) return x;
                                  if (currentDiscType === 'percent') {
                                    return { ...x, discountPercent: Number(v || 0), discountAmount: 0 };
                                  }
                                  return { ...x, discountAmount: Number(v || 0), discountPercent: 0 };
                                }));
                              }}
                            />
                            <select
                              className="form-control"
                              style={{ ...controlStyle, width: '100%' }}
                              value={currentDiscType}
                              onChange={(e) => {
                                const newType = e.target.value;
                                const raw = Number(discValue || 0);
                                setCart((prev) => prev.map((x) => {
                                  if (String(x.itemId) !== String(l.itemId)) return x;
                                  if (newType === 'percent') {
                                    const pct = Math.max(0, Math.min(100, raw));
                                    return { ...x, discountPercent: pct, discountAmount: 0 };
                                  }
                                  return { ...x, discountAmount: Math.max(0, raw), discountPercent: 0 };
                                }));
                              }}
                              aria-label="Discount type"
                            >
                              <option value="amount">₹</option>
                              <option value="percent">% </option>
                            </select>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          <div style={{ borderTop:'1px dashed #e5e7eb', margin:'8px 0' }} />
          {/* Compact controls with label; final summary shown in breakup below */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
            <div style={{ color:'#6b7280', fontSize: 12 }}>Discount</div>
            <div style={{ display:'flex', justifyContent:'flex-end', alignItems:'center', gap:6 }}>
            <input
              className="form-control"
              style={{ ...controlStyle, width: 90, textAlign:'right' }}
              type="number"
              min="0"
              placeholder="Discount"
              step={discountType === 'percent' ? '1' : '0.01'}
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
            />
            <select
              className="form-control"
              style={{ ...controlStyle, width: 90 }}
              value={discountType}
              onChange={(e) => setDiscountType(e.target.value)}
              aria-label="Discount type"
            >
              <option value="amount">₹</option>
              <option value="percent">% </option>
            </select>
            </div>
          </div>
          {!!companyData?.gstin && cart.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, color:'#374151' }}>GST Breakup ({taxTotals.supplyType === 'INTER' ? 'Interstate (IGST)' : 'Intrastate (CGST + SGST)'})</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:6, fontSize: 12, background:'#fafafa', border:'1px solid #e5e7eb', borderRadius:8, padding:8 }}>
                <div style={{ color:'#6b7280' }}>Total</div><div>{formatINR(grossTotal)}</div>
                <div style={{ color:'#6b7280' }}>Item Discounts</div><div style={{ fontWeight: 700, color:'#ef4444' }}>-{formatINR(lineDiscountTotal)}</div>
                <div style={{ color:'#6b7280' }}>Doc Discount</div><div style={{ fontWeight: 700, color:'#ef4444' }}>-{formatINR(discountAmount)}</div>
                <div style={{ color:'#6b7280' }}>Taxable</div><div>{formatINR(taxTotals.taxable)}</div>
                {taxTotals.supplyType !== 'INTER' && (<><div style={{ color:'#6b7280' }}>CGST</div><div>{formatINR(taxTotals.cgst)}</div></>)}
                {taxTotals.supplyType !== 'INTER' && (<><div style={{ color:'#6b7280' }}>SGST</div><div>{formatINR(taxTotals.sgst)}</div></>)}
                {taxTotals.supplyType === 'INTER' && (<><div style={{ color:'#6b7280' }}>IGST</div><div>{formatINR(taxTotals.igst)}</div></>)}
                <div style={{ color:'#6b7280' }}>Cess</div><div>{formatINR(taxTotals.cess)}</div>
                <div style={{ fontWeight: 600 }}>Grand Total</div><div style={{ fontWeight: 800 }}>{formatINR(taxTotals.grand)}</div>
              </div>
            </div>
          )}
          <div style={{ marginTop: 10, display:'flex', gap:8, justifyContent:'flex-end' }}>
            <button
              className="btn btn-primary"
              style={smallBtn}
              onClick={() => {
                if (!customerId) {
                  setCustomerHighlight(true);
                  window.clearTimeout(window.__posCustomerHiT);
                  window.__posCustomerHiT = window.setTimeout(() => setCustomerHighlight(false), 2000);
                  return;
                }
                const today = new Date().toISOString().slice(0,10);
                setPayModal({ open: true, paymentMethod: '', paymentReference: '', paymentDate: today, proofUrl:'', uploading:false });
              }}
              disabled={cart.length === 0}
            >
              Checkout
            </button>
          </div>

          <BillingHistoryPanel
            customerId={customerId}
            refreshKey={historyRefreshKey}
            onPrintPosBill={openPosBillFromInvoice}
          />
        </div>
      </div>

      {/* Edit Modal (no submission yet) */}
      {!!selected && (
        <EditItemModal
          item={selected}
          categories={categories}
          onChange={(field, value) => setSelected((s) => ({ ...s, [field]: value }))}
          images={Array.isArray(selected?.images) ? selected.images : []}
          onAddImage={handleAddItemImage}
          onRemoveImage={handleRemoveItemImage}
          saving={itemImageUploading}
          onClose={() => setSelected(null)}
          onSave={async (payload) => {
            try {
              if (payload.id) {
                await ItemsAPI.update(payload.id, payload);
              } else {
                await ItemsAPI.create({ ...payload, companyId: company?.id });
              }
              await fetchItems();
              setSelected(null);
            } catch (e) {
              console.error('Save item failed', e);
            }
          }}
        />
      )}
      <input
        ref={itemImageInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleItemImageFile}
      />
      {/* Customer inline forms shown above inside Billing Panel */}
      {/* Payment Modal */}
      <PaymentModal
        isOpen={payModal.open}
        onClose={() => setPayModal({ open: false })}
        onSubmit={handlePaymentSubmit}
        title="Record Payment"
        submitText="Record Payment"
        submitLoadingText="Processing..."
        isSubmitting={false}
      />

      {/* Old modal removed - replaced with PaymentModal component above */}
      {billPreview.open && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div className="pos-print-modal-content" style={{ background:'#fff', padding:12, borderRadius:12, width:840, maxWidth:'95%', maxHeight:'92vh', overflow:'auto' }}>
            <style>{`
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                body * { visibility: hidden !important; }
                .pos-print-area, .pos-print-area * { visibility: visible !important; }
                .no-print { display: none !important; }
                @page { margin: 4mm; }
                html, body { width: 100%; }
                .pos-print-modal-content {
                  max-height: none !important;
                  overflow: visible !important;
                  width: auto !important;
                }
                .pos-print-area { position: static !important; margin: 0 auto; display: block !important; }
                .pos-bill { width: 100% !important; max-width: 320px !important; font-size: 11px; color: #111827; box-sizing: border-box; }
                .pos-bill h1, .pos-bill h2, .pos-bill h3 { margin: 0; }
                .pos-bill hr { border: none; border-top: 1px dashed #9ca3af; margin: 4px 0; }
                .pos-bill table { width: 100%; border-collapse: collapse; }
                .pos-bill th, .pos-bill td { padding: 1.2mm 0; font-size: 10px; line-height: 1.15; }
                .pos-bill th { text-align: left; }
                .pos-bill td.qty, .pos-bill td.right { text-align: right; }
              }
            `}</style>
            <div className="no-print" style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <h3 style={{ margin:0 }}>Receipt {billPreview.invoiceNumber ? `- ${billPreview.invoiceNumber}` : ''}</h3>
              <div style={{ display:'flex', gap:8 }}>
                <button className="btn btn-secondary" onClick={() => window.print()}>Print</button>
                <button className="btn btn-secondary" onClick={() => setBillPreview({ open:false, invoiceId:null, invoiceNumber:'', snapshot:null })}>Close</button>
              </div>
            </div>
            {(() => {
              const snap = billPreview.snapshot || {};
              const money = (v) => {
                try { return new Intl.NumberFormat('en-IN', { style:'currency', currency:'INR' }).format(Number(v||0)); } catch { return v; }
              };
              const fmtDT = (iso) => {
                if (!iso) return '';
                try {
                  const d = new Date(iso);
                  if (Number.isNaN(d.getTime())) return String(iso);
                  return d.toLocaleString('en-IN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
                } catch { return String(iso); }
              };
              const companyName = (companyData?.name || snap.company?.name || (company && (company.name || company.businessName)) || 'Company');
              const companyGstin = String(companyData?.gstin || snap.company?.gstin || '').trim();
              const companyStateCode = companyData?.stateCode || snap.company?.stateCode || '';
              const companyState = getStateDisplayName(companyStateCode);
              const companyWebsite = (companyData?.website || snap.company?.website || company?.website || '');
              const companyPhone = (companyData?.phone || snap.company?.phone || company?.phone || '');
              const companyEmail = (companyData?.email || snap.company?.email || company?.email || '');
              const companyAddress = (() => {
                const a = companyData?.address || snap.company?.address || company?.address;
                if (!a) return '';
                if (typeof a === 'string') return a;
                if (typeof a === 'object') {
                  const parts = [a.addressLine1, a.addressLine2, a.city, a.state, a.pincode, a.zip, a.country].filter(Boolean);
                  if (parts.length) return parts.join(', ');
                  const generic = Object.values(a || {}).filter(v => typeof v === 'string').join(', ');
                  return generic;
                }
                return '';
              })();
              const customerName = snap.customer?.name || 'Customer';
              const customerLine = [snap.customer?.email, snap.customer?.phone].filter(Boolean).join(' • ');
              const customerGstin = String(snap.customer?.gstin || '').trim();
              const customerState = getStateDisplayName(snap.customer?.stateCode || '');
              const billedBy = snap.billedBy || '';
              const invoiceNumberInline = billPreview.invoiceNumber || '';
              const printedAt = snap.printedAt || '';
              const paidAt = snap.paymentDateTime || '';
              const method = snap.paymentMethod || '';
              const placeOfSale = getStateDisplayName(snap.posStateCode || companyStateCode || '');
              const companyMeta = [];
              if (companyGstin) companyMeta.push(`GSTIN: ${companyGstin}`);
              if (companyState) companyMeta.push(`State: ${companyState}`);
              const customerMeta = [];
              if (customerGstin) customerMeta.push(`GSTIN: ${customerGstin}`);
              if (customerState) customerMeta.push(`State: ${customerState}`);
              return (
                <>
                  <div className="pos-bill pos-print-area" style={{ margin:'0 auto', width:'100%', maxWidth:320 }}>
                    <div style={{ textAlign:'center' }}>
                      <div style={{ fontWeight:600, fontSize:12 }}>Receipt</div>
                    </div>
                    <div style={{ marginTop: 4, fontSize: 9, color:'#6b7280', display:'grid', gridTemplateColumns:'1fr 1fr', gap: 2 }}>
                      <div>Bill No: {invoiceNumberInline || '-'}</div>
                      <div style={{ textAlign:'right' }}>Printed: {printedAt ? fmtDT(printedAt) : '-'}</div>
                      <div>Paid at: {paidAt ? fmtDT(paidAt) : '-'}</div>
                      <div style={{ textAlign:'right' }}>Method: {method || '-'}</div>
                      <div>Place of Sale: {placeOfSale || '-'}</div>
                      <div style={{ textAlign:'right' }}>Billed by: {billedBy || '-'}</div>
                    </div>
                    <div style={{ border:'1px solid #e5e7eb', borderRadius:6, padding:6, marginTop:6, marginBottom:6, display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
                      <div>
                        <div style={{ fontWeight:600 }}>{companyName}</div>
                        {companyMeta.length > 0 && <div style={{ color:'#6b7280', fontSize:10 }}>{companyMeta.join(' • ')}</div>}
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ fontWeight:600 }}>{customerName}</div>
                        {!!customerLine && <div style={{ color:'#6b7280', fontSize:9 }}>{customerLine}</div>}
                        {customerMeta.length > 0 && <div style={{ color:'#6b7280', fontSize:9 }}>{customerMeta.join(' • ')}</div>}
                      </div>
                    </div>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontVariantNumeric:'tabular-nums' }}>
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th className="right" style={{ textAlign:'right' }}>Qty</th>
                          <th className="right" style={{ textAlign:'right' }}>Price</th>
                          <th className="right" style={{ textAlign:'right' }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(snap.items || []).map((l, i) => (
                          <tr key={i}>
                            <td>{l.name}</td>
                            <td className="right" style={{ textAlign:'right' }}>{l.qty}</td>
                            <td className="right" style={{ textAlign:'right' }}>{money(l.price)}</td>
                            <td className="right" style={{ textAlign:'right' }}>{(() => {
                              const qty = Number(l.qty || 0);
                              const price = Number(l.price || 0);
                              const gross = qty * price;
                              const discAmt = Math.max(0, Number(l.discountAmount || 0));
                              const discPct = Math.max(0, Math.min(100, Number(l.discountPercent || 0)));
                              const discountFromPercent = (discPct / 100) * gross;
                              const net = Math.max(0, gross - discAmt - discountFromPercent);
                              return money(net);
                            })()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <hr />
                    <table style={{ width:'100%', borderCollapse:'collapse', fontVariantNumeric:'tabular-nums' }}>
                      <tbody>
                        {(() => {
                          const gross = Number(snap.grossTotal || 0);
                          const itemDisc = Number(snap.lineDiscountTotal || 0);
                          const docDisc = Number(snap.docDiscountAmount || snap.discountAmount || 0);
                          const subtotalAfterItems = Math.max(0, gross - itemDisc);
                          return (
                            <>
                              <tr><td>Total</td><td className="right" style={{ textAlign:'right' }}>{money(gross)}</td></tr>
                              <tr><td>Item Discounts</td><td className="right" style={{ textAlign:'right', color:'#ef4444', fontWeight:600 }}>-{money(itemDisc)}</td></tr>
                              <tr><td>Subtotal</td><td className="right" style={{ textAlign:'right' }}>{money(subtotalAfterItems)}</td></tr>
                              <tr><td>Doc Discount</td><td className="right" style={{ textAlign:'right', color:'#ef4444', fontWeight:600 }}>-{money(docDisc)}</td></tr>
                            </>
                          );
                        })()}
                        <tr><td>Taxable</td><td className="right" style={{ textAlign:'right' }}>{money(snap.taxTotals?.taxable || 0)}</td></tr>
                        <tr><td>CGST</td><td className="right" style={{ textAlign:'right' }}>{money(snap.taxTotals?.cgst || 0)}</td></tr>
                        <tr><td>SGST</td><td className="right" style={{ textAlign:'right' }}>{money(snap.taxTotals?.sgst || 0)}</td></tr>
                        <tr><td>IGST</td><td className="right" style={{ textAlign:'right' }}>{money(snap.taxTotals?.igst || 0)}</td></tr>
                        <tr><td>Cess</td><td className="right" style={{ textAlign:'right' }}>{money(snap.taxTotals?.cess || 0)}</td></tr>
                        <tr><td style={{ fontWeight:600 }}>Grand Total</td><td className="right" style={{ textAlign:'right', fontWeight:700 }}>{money(snap.taxTotals?.grand || 0)}</td></tr>
                      </tbody>
                    </table>
                    <hr />
                    <div style={{ textAlign:'center', fontSize:9, marginBottom:4 }}>Thank you!</div>
                    {/* Footer company contact at extreme bottom */}
                    <div style={{ fontSize:9, color:'#6b7280', marginTop:4 }}>
                      {!!companyAddress && <div style={{ marginTop:2 }}>{companyAddress}</div>}
                      <div style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
                        <span>{companyPhone || ''}</span>
                        <span>{companyEmail || ''}</span>
                        <span style={{ overflowWrap:'anywhere' }}>{companyWebsite || ''}</span>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}


